import { AfterViewInit, Component, ElementRef, inject, Injector, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { ApplicationContext, RecentApp, ThirdPartyCredentialType, UserData, UserProfile } from 'src/app/models';
import { AlertModel } from 'src/app/models/alert.model';
import { LoanDocDashboardTask } from 'src/app/models/borrower/loan-doc-dashboard-task.model';
import { MentionMessageScope } from 'src/app/models/mention.model';
import { TpoConfiguration } from 'src/app/modules/admin/tpo-config/models/tpo-configuration.model';
import { CreditCredentialsConfigurationDialogComponent } from 'src/app/modules/credit-credentials-configuration/credit-credentials-configuration-dialog/credit-credentials-configuration-dialog.component';
import { CreditConfigService } from 'src/app/modules/global-admin/credit-config/services/credit-config.service';
import { AuthService } from 'src/app/services/auth.service';
import { CommonService } from 'src/app/services/common.service';
import { Constants } from 'src/app/services/constants';
import { FirebaseService } from 'src/app/services/firebase';
import { NotificationService } from 'src/app/services/notification.service';
import { SignalRService } from 'src/app/services/signalr.service';
import { LoanUpdateCoordinatorService } from 'src/app/services/loan-update-coordinator.service';
import { TaskService } from 'src/app/services/task.service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import { DrawerOptions, DrawerService, DrawerSize } from 'src/app/shared/services/drawer.service';
import { Notifications } from '../../admin/header/header-actions/header-actions.component';
import { MentionViewModel } from 'src/app/shared/components/mentions/mention-item/mention-item.component';
import { ConversationService } from 'src/app/modules/conversations/services/conversation.service';
import { SystemLevelService } from 'src/app/services/system-level.service';

@Component({
    selector: 'tpo-layout',
    templateUrl: 'tpo-layout.component.html',
    styleUrls: ['./tpo-layout.component.scss'],
    standalone: false
})
export class TpoLayoutComponent
  extends ApplicationContextBoundComponent
  implements AfterViewInit {
  // The sticky header (impersonation banner + topbar) is measured rather than assumed: company TPO theme CSS
  // can resize the topbar (PRMG sets it to 100px), and a stale offset parks the breadcrumb / loan summary bar
  // underneath the header, where it is hidden by the header's higher z-index.
  @ViewChild('stickyHeader', { static: true })
  private _stickyHeader: ElementRef<HTMLElement>;

  private readonly _hostElement: ElementRef<HTMLElement> = inject(ElementRef);
  private _stickyHeaderResizeObserver: ResizeObserver;

  currentlyLoggedInUser: UserData;
  currentlyLoggedInUserProfile: UserProfile;
  recentApps: Array<RecentApp> = [];
  companyLogoUrl: string = '';
  userAvatarUrl: string;
  isNewApplicationEnabled: boolean;

  overrideCssUrl: string = null;

  tpoConfig: TpoConfiguration;

  currentYear: number;

  notifications: Notifications = null;

  mentionsDrawerOptions: DrawerOptions = {
    size: DrawerSize.Medium,
    containerWrapperId: null,
  };

  alertsDrawerOptions: DrawerOptions = {
    size: DrawerSize.Medium,
    containerWrapperId: null,
  };

  loanActivityDrawerOptions: DrawerOptions = {
    size: DrawerSize.Medium,
    containerWrapperId: null,
  };

  taskEditorDrawerOptions: DrawerOptions = {
    size: DrawerSize.XXLarge,
    containerWrapperId: null,
  };

  mentionLoanDocTask: LoanDocDashboardTask | null = null;
  mentionMessageScope: MentionMessageScope;
  messageId: number | null = null;
  refreshMentions: boolean;
  mentionApplicationId: number | null = null;
  mentionTrackingGuid: string;
  taskMentionTrackingUrl: string;
  userId: string = '';

  protected faviconUrl: string;

  protected userIsImpersonating: boolean = true;
  protected isImpersonatingForLoan: boolean = false;

  protected isCompanySmartfi: boolean = false;

  private _applicationContextSubscription: Subscription;
  private _activatedRouteSubscription: Subscription;
  private _routerEventsSubscription: Subscription;

  private _mentionsDrawerOpened: boolean = false;
  private _notificationsDrawerOpened: boolean = false;

  constructor(
    private readonly injector: Injector,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _authService: AuthService,
    private readonly _spinner: GlobalSpinnerService,
    private readonly _environmentService: EnvironmentService,
    private readonly _signalRService: SignalRService,
    private readonly _loanUpdateCoordinator: LoanUpdateCoordinatorService,
    private readonly _firebaseService: FirebaseService,
    private readonly _environment: EnvironmentService,
    private readonly _taskService: TaskService,
    private readonly _modalService: NgbModal,
    private readonly _router: Router,
    private readonly _notifyService: NotificationService,
    private readonly _drawerService: DrawerService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _creditConfigService: CreditConfigService,
    private readonly _conversationService: ConversationService,
    private readonly _commonService: CommonService,
    private readonly _systemLevelService: SystemLevelService
  ) {
    super(injector);

    this._applicationContextSubscription =
      this.applicationContextService.context.subscribe(async (context) => {
        this.isCompanySmartfi = context.isCompanySmartfi;
        const company = context.globalConfig.company.find(
          (c) => c.companyId == context.userPermissions.companyId
        );
        this.overrideCssUrl =
          this._environmentService.apiInfo.publicApiBaseUrl +
          `api/PublicApi/custom-css/${company.companyGUID}/tpo`;
        this.tpoConfig = context.tpoConfig;
        this.currentYear = new Date().getFullYear();
        this.userId = context.userPermissions.userId;

        this._loanUpdateCoordinator.initialize();
        this._signalRService.run(context);
        this._firebaseService.run(context);
      });

    this.faviconUrl = this._environment.faviconUrl;
  }

  ngOnInit() {
    const authData = this._localStorageService.authorizationData;
    this.isImpersonatingForLoan = authData && !!authData.impersonatingForLoanId;
    this.userIsImpersonating = authData && (!!authData.impersonatingForLoanId || !!authData.impersonatingAsAdmin);

    // Subscribe to router events to scroll to top on navigation
    this._routerEventsSubscription = this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      window.scrollTo(0, 0);
    });

    this._activatedRouteSubscription = this._activatedRoute.data.subscribe(
      (data) => {
        this.companyLogoUrl =
          this._environmentService.apiInfo.apiBaseUrl +
          `company/br/logo/${this.applicationContext.userPermissions.companyId}`;
        this.currentlyLoggedInUser = this.applicationContext.currentlyLoggedInUser;
        this.currentlyLoggedInUserProfile = this.applicationContext.currentlyLoggedInUserProfile.userProfile;
        this.isNewApplicationEnabled = this.applicationContext.userPermissions.showNewSubmissionForm;
        if (this.currentlyLoggedInUser.avatar === 'assets/images/male.png') {
          this.userAvatarUrl = this.currentlyLoggedInUser.avatar;
        } else {
          this.userAvatarUrl = this._environmentService.apiInfo.apiBaseUrl +
            this.currentlyLoggedInUser.avatar;
        }
      }
    );

    this.recentApps =
      this.applicationContext.userPermissions.recentApplications;
    const body: HTMLBodyElement | null = document.querySelector('#loda-body');
    if (body) {
      if (this.applicationContext.isCompanyPulseProcessing) {
        body.classList.add('pulse-processing-tpo-background');
      } else {
        body.classList.add('tpo-background');
      }
    }
  }

  async ngAfterViewInit() {
    this.observeStickyHeaderHeight();

    if ( this.tpoConfig.enableCreditCredentialsWizard) {
      setTimeout(async () => {
        await this.checkIfCreditCredentialsAreSetup(this.applicationContext);
      }, 1000);
    }
  }

  // Publishes the sticky header's rendered height as --tpo-sticky-header-height on the layout host, so every
  // sticky element below it (breadcrumb / loan summary bar, screen action bars) parks right underneath it.
  // Written straight to the host style: ResizeObserver callbacks don't trigger change detection.
  private observeStickyHeaderHeight = () => {
    const header = this._stickyHeader?.nativeElement;
    if (!header || typeof ResizeObserver === 'undefined') {
      return;
    }
    const publish = () =>
      this._hostElement.nativeElement.style.setProperty('--tpo-sticky-header-height', `${header.getBoundingClientRect().height}px`);
    this._stickyHeaderResizeObserver = new ResizeObserver(publish);
    this._stickyHeaderResizeObserver.observe(header);
    publish();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._stickyHeaderResizeObserver?.disconnect();
    this._applicationContextSubscription?.unsubscribe();
    this._activatedRouteSubscription?.unsubscribe();
    this._routerEventsSubscription?.unsubscribe();
  }

  onLogoutClicked = () => {
    const body: HTMLBodyElement | null = document.querySelector('#loda-body');
    if (body) {
      body.classList.add('tpo-background');
    }
    this._spinner.show();
    this._authService.logout().subscribe(
      () => {
        this._spinner.hide();
      },
      (error) => {
        this._spinner.hide();
      }
    );
  };

  onMentionClicked = (mention: MentionViewModel) => {
    this.mentionMessageScope = mention.messageScope;
    this.messageId = mention.messageId;
    this.refreshMentions = !mention.openDate; // reload mentions if unread

    if (this.mentionMessageScope === MentionMessageScope.InternalMessage) {
      this.mentionApplicationId = mention.entityId;
      if (mention.trackingGuid) {
        this.mentionTrackingGuid = mention.trackingGuid;
      }
      this._drawerService.hide('taskEditorDrawer', 10);
      this._drawerService.show('loanActivityInternalMessageDrawer', 10);
    } else if (this.mentionMessageScope === MentionMessageScope.TaskNote) {
      this._taskService.getTaskDashboardViewById(mention.entityId).subscribe(
        (task) => {
          this.mentionLoanDocTask = task;
          if (mention.trackingGuid) {
            this.taskMentionTrackingUrl = `${this._environment.apiInfo.apiBaseUrl}mentions/pixel/${this.userId}/${mention.trackingGuid}`;
          }
          this._drawerService.hide('loanActivityInternalMessageDrawer', 10);
          this._drawerService.show('taskEditorDrawer', 10);
        },
        (error) => {
          this._notifyService.showError(error.error, 'Error!');
        }
      );
    } else if (this.mentionMessageScope === MentionMessageScope.BorrowerMessage) {
      this._conversationService.addBorrowerChat(
        mention.applicationId,
        mention.borrowerId,
        { senderDisplayName: "" },
        true,
        false,
        mention.conversationId
      );
    }
  };

  onAlertsCreated = (alerts: AlertModel[]) => {
    alerts.forEach((a) => {
      const bucketThatAlertNeedsToGoTo = this.notifications?.alertBuckets?.find(
        (b) => b.type == a.alertType
      );
      if (bucketThatAlertNeedsToGoTo) {
        bucketThatAlertNeedsToGoTo.alerts.splice(0, 0, a);
      }
      const updatedNotifications = { ...this.notifications };
      this.notifications = updatedNotifications as Notifications;
    });
  };

  onNotificationsClicked = (notifications: Notifications) => {
    this.notifications = notifications;
    if (this._mentionsDrawerOpened) {
      this._drawerService.hide('mentionsDrawer', 10);
    }
    if (notifications && !this._notificationsDrawerOpened) {
      this._drawerService.show('alertsDrawer', 10);
      this._notificationsDrawerOpened = true;
    } else {
      this._drawerService.hide('alertsDrawer', 10);
    }
  }

  onMentionsClicked = (open: boolean) => {
    if (this._notificationsDrawerOpened) {
      this._drawerService.hide('alertsDrawer', 10);
    }
    if (open && !this._mentionsDrawerOpened) {
      this._drawerService.show('mentionsDrawer', 10);
      this._mentionsDrawerOpened = true;
    } else {
      this._drawerService.hide('mentionsDrawer', 10);
    }
  }

  notificationDetails = (alert: AlertModel) => {
    if (alert && alert.applicationId && alert.taskId) {
      //Open the task in the side panel
      this._taskService.getTaskDashboardViewById(alert.taskId).subscribe(
        (task) => {
          this.mentionLoanDocTask = task;
          this.mentionMessageScope = MentionMessageScope.TaskNote;
          this._drawerService.show(
            'taskEditorDrawer',
            10,
            undefined,
            undefined,
            {
              size: DrawerSize.XXLarge,
              containerWrapperId: null,
            }
          );
        },
        (error) => {
          this._notifyService.showError(error.error, 'Error!');
        }
      );
    } else if (alert && alert.applicationId && alert.borrowerId) {
      this._router.navigate([`tpo/app-details/${alert.applicationId}`]);
    }
  };


  onNotificationsDrawerClosed = () => {
    this._notificationsDrawerOpened = false;
  };

  onMentionsDrawerClosed = () => {
    this._mentionsDrawerOpened = false;
  };

  onMentionDetailsDrawerClosed = () => { };

  protected onStopImpersonatingClicked = () => {
    this.userIsImpersonating = false;
    this._router.navigate(['end-impersonation-mode'], {
      queryParams: {},
    });
  };

  private checkIfCreditCredentialsAreSetup = async (
    context: ApplicationContext
  ) => {
    const creditVendorForProfile =
      context.currentlyLoggedInUserProfile.thirdPartyCredentials.find(
        (c) => c.credentialType === ThirdPartyCredentialType.CreditVendor
      );
    if (creditVendorForProfile) {
      return;
    }
    let needToShowCreditConfigPopup = true;
    const webPreferences = await firstValueFrom(
      this._commonService.getWebPreferences()
    ).catch(() => null);
    if (webPreferences?.hideCreditCredentialsConfigurationPrompt) {
      needToShowCreditConfigPopup = false;
    }
    const restrictToEncompassCredit =
      await firstValueFrom(this._systemLevelService.isRestrictCreditVendorsToEncompassCreditEnabled());
    // If the setting can't be read we can't tell whether this prompt applies, so skip it rather than nag.
    if (restrictToEncompassCredit === null) {
      needToShowCreditConfigPopup = false;
    } else if (restrictToEncompassCredit) {
      const creditConfig = await this._creditConfigService.getTpoGlobalCreditConfigList();
      if (!creditConfig) {
        needToShowCreditConfigPopup = false;
      }
    }
    if (needToShowCreditConfigPopup) {
      let modalRef = this._modalService.open(
        CreditCredentialsConfigurationDialogComponent,
        {
          ...Constants.modalOptions.large,
          windowClass: 'myCustomModalClass',
        }
      );
      modalRef.componentInstance.showDoNotShowAgainOption = true;

      modalRef.result.then(
        () => { },
        () => { }
      );
    }
  };
}
